import { useState, useEffect, useRef } from 'react';
import styles from './Scanner.module.css';

const signatures = [
    {
        name: "Remote Code Execution",
        regex: /loadstring|getfenv|setfenv|xpcall\(_G/gi,
        severity: "high",
        desc: "Attempts to run hidden or outside code.",
        detail: "This script is trying to download and run code from the internet or a hidden source. This is a common way for hackers to update a script into a virus after you've already started using it."
    },
    {
        name: "Discord Webhook",
        regex: /https?:\/\/(?:ptb\.|canary\.)?discord(?:app)?\.com\/api\/webhooks\/\d+\/[\w-]+/gi,
        severity: "critical",
        desc: "Direct link to a Discord server found.",
        detail: "The script is connected to a Discord 'Webhook.' This is usually used to send your private information (like your username or items) directly to a hacker's Discord server."
    },
    {
        name: "IP & Location Tracker",
        regex: /ip-api\.com|api\.ipify\.org|checkip\.dyndns/gi,
        severity: "critical",
        desc: "Script is trying to find where you live.",
        detail: "This script is contacting a service to find your IP address. This can be used to see your general city and state, which is a major privacy risk."
    },
    {
        name: "Hidden Module Loading",
        regex: /require\(\s*\d+\s*\)/g,
        severity: "high",
        desc: "Loads code from a hidden Roblox asset ID.",
        detail: "The script 'requires' a specific ID. Because that ID's code is hosted on Roblox's servers and not shown here, it could be doing anything—including stealing your data—without you seeing it."
    },
    {
        name: "Scrambled Text (Hex/Uni)",
        regex: /\\x[0-9A-Fa-f]{2}|\\u[0-9A-Fa-f]{4}/g,
        severity: "medium",
        desc: "Uses 'Hex' encoding to hide links or words.",
        detail: "The script uses letter/number sequences like \\x68 to hide malicious web links. While sometimes used by legitimate developers, it's often a red flag in free scripts."
    },
    {
        name: "External Data Sending",
        regex: /HttpService|PostAsync|GetAsync|request\s*\(/gi,
        severity: "medium",
        desc: "Can send your data to an outside website.",
        detail: "This script has the ability to 'talk' to the internet. While some scripts use this for legitimate reasons, it is the primary way hackers steal your items or account info."
    },
    {
        name: "Setting Manipulation",
        regex: /getfenv|setfenv|debug\./gi,
        severity: "high",
        desc: "Trying to mess with the script's 'brain'.",
        detail: "The script is reaching into the core settings of the game environment. This is often done to hide the script's activity from other security tools or to bypass Roblox's built-in protections."
    },
    {
        name: "Locked Script (Obfuscated)",
        regex: /LPH_|__LURAPH|IB_INTERNAL/gi,
        severity: "critical",
        desc: "This script is locked/encrypted (Luraph/IronBrew).",
        detail: "The developer has 'locked' this script so no one can read how it works. While some protect their work this way, most malicious scripts use this so you can't see the virus inside. Use with extreme caution."
    },
    {
        name: "Forced Teleport",
        regex: /TeleportService|TeleportToPlaceInstance/gi,
        severity: "medium",
        desc: "Can force you to join a different game.",
        detail: "This allows the script to kick you out of your current game and force you into another one. This is frequently used in 'Phishing' scams to send you to a fake version of a game designed to steal your password."
    },
    {
        name: "Backwards Text Evasion",
        regex: /\w+:reverse\(\)/gi,
        severity: "high",
        desc: "Detected backwards text (e.g., 'daol').",
        detail: "The script writes its commands backwards (like 'gnirtsdaol') and then tells the computer to flip them. This is a very common trick to hide 'loadstring' from scanners."
    },
    {
        name: "Cookie/Account Stealer",
        regex: /\.ROBLOSECURITY|_ga\b|_gid\b/gi,
        severity: "critical",
        desc: "Trying to steal your login session!",
        detail: "DANGER: This script is looking for your '.ROBLOSECURITY' cookie. If a hacker gets this, they can log into your account instantly without needing your password or 2FA."
    },
    {
        name: "PC File Access",
        regex: /readfile|writefile|listfiles|appendfile/gi,
        severity: "high",
        desc: "Trying to read or save files on your computer.",
        detail: "This script is attempting to look at the files on your actual computer (not just the game). This is usually only possible on high-level executors, but it is a huge security risk for your personal files."
    },
    {
        name: "Game Logic Hooking",
        regex: /getrawmetatable|setreadonly|make_writeable/gi,
        severity: "high",
        desc: "Modifying the game's core rules.",
        detail: "The script is 'hooking' into the game's foundation. While common for 'God Mode' or 'Infinite Ammo' cheats, it is also used by advanced malware to hide itself from the game's own security."
    },
    {
        name: "Log Flooding / Anti-Log",
        regex: /:rep\(100\)|rconsoleclear/gi,
        severity: "medium",
        desc: "Trying to hide its tracks in the console.",
        detail: "The script is trying to clear the output log or flood it with useless text. It does this so you won't see error messages or warnings that might tell you it's doing something bad."
    },
    {
        name: "Function Hooking",
        regex: /hookfunction|newcclosure|clonefunction|hookmetamethod/gi,
        severity: "high",
        desc: "Replacing game functions with fake ones.",
        detail: "This script is swapping out real game functions with its own. Hackers use this to secretly intercept what you type, click, or do in-game, and redirect that information to themselves."
    },
    {
        name: "Teleport Persistence",
        regex: /queue_on_teleport|queueonteleport/gi,
        severity: "critical",
        desc: "Script survives even if you join a new game.",
        detail: "This makes the script run again automatically after a forced teleport. Even if you get sent to a different game, this code keeps executing. It's almost always used to maintain a hack or data stealer across sessions."
    },
    {
        name: "Clipboard Access",
        regex: /setclipboard|getclipboard|toclipboard|set_clipboard/gi,
        severity: "high",
        desc: "Can read or overwrite what you've copied.",
        detail: "Your clipboard holds everything you copy (Ctrl+C), including passwords, emails, and crypto wallet addresses. This script can read or replace that data without you knowing."
    },
    {
        name: "Input Simulation",
        regex: /keypress|keyrelease|mouse1click|mouse2click|mouse1press|mouse1release|mouse2press|mouse2release|mousescroll/gi,
        severity: "medium",
        desc: "Simulates mouse clicks and keyboard presses.",
        detail: "This script can fake mouse clicks and key presses on your computer. This is used for auto-farming cheats, but malicious scripts can use it to interact with things on your screen without your permission."
    },
    {
        name: "Hidden Instance Scan",
        regex: /getinstances|getnilinstances|getloadedmodules|getrunningscripts|getscripts/gi,
        severity: "high",
        desc: "Scanning every object hidden in the game.",
        detail: "This grabs a list of ALL objects in the game, including hidden ones that normal scripts can't see. Hackers use this to find anti-cheat systems, hidden remotes, or security scripts to disable them."
    },
    {
        name: "Hidden Property Access",
        regex: /gethiddenproperty|sethiddenproperty/gi,
        severity: "high",
        desc: "Accessing properties that are normally invisible.",
        detail: "Roblox hides certain internal properties from regular scripts. This function lets the script read and change them anyway. This is almost exclusively an exploit-level ability with no normal use case."
    },
    {
        name: "Silent Event Firing",
        regex: /fireremoteevent|invokeremotefunction|firesignal|fireclickdetector|firetouchinterest|fireproximityprompt/gi,
        severity: "high",
        desc: "Triggering game events the server can't verify.",
        detail: "These functions fire game events (like buying items or opening doors) silently and instantly. Malicious scripts use this to duplicate items, unlock paid features, or spam the server with fake actions."
    },
    {
        name: "Function Internals Access",
        regex: /getupvalues|setupvalue|getupvalue|getprotos|getproto\b/gi,
        severity: "high",
        desc: "Reaching inside other functions to change them.",
        detail: "This lets the script read and rewrite the internal variables of other functions. Hackers use this to break anti-cheat logic, disable security checks, or hijack existing game code without touching the original function."
    },
    {
        name: "Environment Dumping",
        regex: /getgenv|getrenv|getsenv|gettenv|getreg\b|getgc\b/gi,
        severity: "high",
        desc: "Dumping the entire game/script environment.",
        detail: "These functions expose every variable, function, and object in the game's memory at once. Hackers dump these to find hidden remotes, secrets, or admin commands that weren't meant to be accessible."
    },
    {
        name: "Executor Detection",
        regex: /identifyexecutor|getexecutorname|is_sirhurt_closure|isexecutorclosure|checkcaller|KRNL_LOADED|FLUXUS_LOADED|FLUXUSZ_LOADED|WAVE_LOADED|DELTA_LOADED|XENO_LOADED|HYDROGEN_LOADED|SOLARA_LOADED|TRIGON_LOADED|SCRIPTWARE|CODEX_LOADED|SIRHURT_LOADED|JJSPLOIT_LOADED|SELIWARE_LOADED|ARCEUS_X|VEGAX_LOADED|ELECTRON_LOADED|CRYPTIC_LOADED|VALEX_LOADED|VOLT_LOADED|MATCHA_LOADED|NEUTRON_LOADED|CALAMARI_LOADED|CELERY_LOADED|PROTOSMASHER_LOADED|SENTINEL_LOADED|OXYGEN_LOADED|COMET_LOADED|NIHON_LOADED|syn\.isclosure/gi,
        severity: "medium",
        desc: "Script is checking which exploit tool you're using.",
        detail: "This checks what executor (hack tool) you're running. Malicious scripts use this to tailor their attacks for specific tools, or to only activate on certain executors to avoid being caught in testing environments."
    },
    {
        name: "Locked Script (Other Obfuscators)",
        regex: /VM_ENCRYPT|BYTECODE_|PROMETHEUS_|CARBON_|__index__vm|SYNAPSE_INTERNAL/gi,
        severity: "critical",
        desc: "Encrypted with a known script locker.",
        detail: "Another form of script encryption beyond Luraph/IronBrew. The script's real code is completely hidden inside a virtual machine or bytecode blob. There is no way to read what it actually does."
    },
    {
        name: "Char Array Obfuscation",
        regex: /string\.char\s*\(\s*(?:\d+\s*,\s*){4,}/g,
        severity: "high",
        desc: "Building hidden text from raw numbers.",
        detail: "The script builds words character-by-character from raw numbers (like string.char(108,111,97,100)). This is a classic trick to hide commands like 'loadstring' or malicious URLs from basic scanners."
    },
    {
        name: "Base64 Decoding",
        regex: /base64\.decode|base64\.Decode|b64decode|fromBase64/gi,
        severity: "high",
        desc: "Decoding a hidden/encoded payload.",
        detail: "The script is decoding a blob of Base64 text, which is a common way to hide an entire malicious script inside what looks like random letters. The decoded result is almost always executed immediately after."
    },
    {
        name: "XOR Encryption",
        regex: /bxor|bit\.bxor|bit32\.bxor/gi,
        severity: "medium",
        desc: "Using XOR math to unscramble hidden code.",
        detail: "XOR is a simple encryption method often used to hide strings or whole scripts. Legitimate scripts almost never need this. In free scripts, it usually means something is being hidden on purpose."
    },
    {
        name: "Raw Script Fetching",
        regex: /pastebin\.com\/raw|raw\.githubusercontent\.com|ghostbin\.com|cdn\.discordapp\.com\/attachments/gi,
        severity: "critical",
        desc: "Pulling live code from the internet to run.",
        detail: "The script is downloading raw code from an external site (Pastebin, GitHub, Discord CDN, etc.) and running it. The developer can silently update what gets run at any time, turning a harmless-looking script into a virus."
    },
    {
        name: "Fast Flag Manipulation",
        regex: /setfflag|getfflag|setfflags/gi,
        severity: "medium",
        desc: "Changing Roblox's internal engine settings.",
        detail: "Fast Flags are Roblox's internal engine toggles. Scripts that change them can disable security features, alter rendering, or unlock experimental behaviors that Roblox deliberately keeps hidden from users."
    },
    {
        name: "Script Decompilation",
        regex: /decompile|dumpstring/gi,
        severity: "high",
        desc: "Trying to read the source code of other scripts.",
        detail: "This attempts to reverse-engineer other scripts in the game, including anti-cheat and server scripts. Hackers use this to understand how the game's security works so they can find holes in it."
    },
    {
        name: "Remote Spy / Event Spy",
        regex: /getconnections|hookfunction.*Remote|RemoteEvent.*hook/gi,
        severity: "high",
        desc: "Spying on all network traffic between you and the server.",
        detail: "A 'Remote Spy' intercepts every message sent between your game client and the server. Hackers use this to steal admin commands, item data, or private player info that flows through the game's network."
    },
    {
        name: "Virtual Console Abuse",
        regex: /rconsolesettitle|rconsolecreate|rconsoleprintraw|rconsolewarn|rconsoleinput/gi,
        severity: "low",
        desc: "Opening a fake console window.",
        detail: "This creates a separate console window outside of the normal Roblox output. It's sometimes used legitimately, but malicious scripts use it to show fake loading screens or hide what the script is actually doing."
    },
    {
        name: "File System Manipulation",
        regex: /makefolder|delfolder|delfile|isfile\b|isfolder\b/gi,
        severity: "high",
        desc: "Creating or deleting files/folders on your PC.",
        detail: "Beyond just reading files, this script can create new folders or delete existing ones on your real computer. This is often used to drop or install additional files without you seeing them."
    },
    {
        name: "Executor Namespace (syn/fluxus/krnl/etc)",
        regex: /\bsyn\.\w+|\bfluxus\.\w+|\bkrnl\.\w+|\bdelta\.\w+|\bxeno\.\w+|\bwave\.\w+|\bhydrogen\.\w+|\bsolara\.\w+|\btrigon\.\w+|\bvegax\.\w+|\belectron\.\w+|\bcelery\.\w+|\bsentinel\.\w+|\bcomet\.\w+|\bEXECUTOR\b/gi,
        severity: "medium",
        desc: "Uses executor-specific APIs directly.",
        detail: "This script calls functions from a specific exploit tool (Synapse, Fluxus, Krnl, Delta, Xeno, Wave, etc). These are powerful APIs that bypass Roblox's normal rules. Their presence confirms this script was written to run in an exploit environment."
    },
    {
        name: "WebSocket Connection",
        regex: /WebSocket|websocket|wss?:\/\/|syn\.websocket|HttpService.*WebSocket/gi,
        severity: "critical",
        desc: "Opening a live two-way connection to a remote server.",
        detail: "WebSockets create a persistent, real-time connection between your game and an outside server. Unlike a one-time HTTP request, this keeps a channel open the entire session — letting a hacker send commands to the script, receive your data continuously, or remotely control what the script does without any visible activity."
    }
];


function Scanner() {
    const [code, setCode] = useState('');
    const [results, setResults] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [status, setStatus] = useState('idle');
    const [expandedIndex, setExpandedIndex] = useState([]);
    const [cooldown, setCooldown] = useState(0);
    const [fileStats, setFileStats] = useState({ lines: 0, threats: 0, time: 0, risk: 0 });

    const resultsRef = useRef(null);

    const toggleAccordion = (index) => {
        setExpandedIndex(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    useEffect(() => {
        if (status === 'completed' && results && resultsRef.current) {
            const yOffset = -100;
            const element = resultsRef.current;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    }, [status, results]);

    useEffect(() => {
        let timer;
        if (status === 'completed' || status === 'copied') {
            timer = setTimeout(() => {
                setStatus('closing');
            }, status === 'copied' ? 2000 : 3000);
        } else if (status === 'closing') {
            timer = setTimeout(() => {
                setStatus('idle');
            }, 300);
        }
        return () => clearTimeout(timer);
    }, [status]);

    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);



    const runScan = (manualCode) => {
        setExpandedIndex([]);
        if (cooldown > 0) return;

        const textToScan = typeof manualCode === 'string' ? manualCode : code;
        if (!textToScan || !textToScan.trim()) return alert("Paste code first!");

        setStatus('scanning');
        const startTime = performance.now();

        setTimeout(() => {
            //const linesArray = textToScan.split('\n');
            //const lineCount = linesArray.length;
            const findings = [];
            const sevWeights = { critical: 100, high: 50, medium: 20, info: 0 };
            let riskScore = 0;
            let foundThreatsCount = 0;
            let maxSevLabel = "info";
            const lines = textToScan.split('\n');

            signatures.forEach(sig => {
                let match;
                const seenMatches = new Set();
                sig.regex.lastIndex = 0;

                while ((match = sig.regex.exec(textToScan)) !== null) {
                    const matchValue = match[0];
                    if (!seenMatches.has(matchValue)) {
                        const charBeforeMatch = textToScan.substring(0, match.index);
                        const lineIndex = charBeforeMatch.split('\n').length - 1;

                        findings.push({
                            ...sig,
                            snippet: lines[lineIndex]?.trim() || matchValue,
                            lineNum: lineIndex + 1
                        });
                        seenMatches.add(matchValue);
                        foundThreatsCount++;

                        if (sevWeights[sig.severity] > riskScore) {
                            riskScore = sevWeights[sig.severity];
                        }

                        if (sevWeights[sig.severity] > sevWeights[maxSevLabel]) {
                            maxSevLabel = sig.severity;
                        }
                    }
                }
            });

            const endTime = performance.now();
            const duration = (endTime - startTime).toFixed(2);

            const criticalThreats = findings.filter(f => f.severity === 'critical');

            setResults({
                findings: findings.sort((a, b) => sevWeights[b.severity] - sevWeights[a.severity]),
                criticals: criticalThreats,
                maxSev: maxSevLabel,
                charCount: textToScan.length,
                scanTime: duration
            });

            setFileStats({
                lines: lines.length,
                threats: foundThreatsCount,
                time: duration,
                risk: riskScore
            });

            setCooldown(10);
            setStatus('completed');
        }, 600);
    };

    const handleFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            setCode(e.target.result);
            runScan(e.target.result);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setStatus('copied');
    };

    return (
        <div className={styles.scannerPage}>
            {status !== 'idle' && (
                <div className={`${styles.notification_toast} ${styles[status]}`}>
                    {status === 'scanning' ? "Analyzing Script Engine..." :
                        status === 'copied' ? "Snippet copied to clipboard!" :
                            "Scan Analysis Complete."}
                </div>
            )}

            <header className={styles.header}>
                <div>
                    <h1 className={styles.h1}><span>SCRIPT</span> SCANNER</h1>
                    <div className={styles.version}>
                        By: <span><a href='https://github.com/ofcWilliam' style={{ color: 'var(--accent)' }}>ofcWilliam</a></span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {/* {results && (
                        <div className={styles.stats} style={{ color: 'var(--safe)', background: 'rgba(0, 255, 136, 0.1)' }}>
                            {results.scanTime}ms
                        </div>
                    )} */}
                    <div className={styles.stats}>
                        {results ? `${results.charCount} Chars` : 'Ready'}
                    </div>
                </div>
            </header>

            <div
                className={`${styles.drop_area} ${isDragging ? styles.dragging : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
                onClick={() => document.getElementById('fileInput').click()}
            >
                <strong className={styles.strong}>{isDragging ? "Drop to Scan" : "Drop Script or Click to Upload"}</strong>
                <input type="file" id="fileInput" hidden onChange={(e) => handleFile(e.target.files[0])} />
            </div>

            <textarea
                className={styles.textarea}
                placeholder="Paste source code here..."
                value={code}
                onChange={(e) => setCode(e.target.value)}
            ></textarea>

            <div className={styles.btn_row}>
                <button className={styles.btn_sub} onClick={() => { setCode(''); setResults(null); }}>Clear</button>
                <button
                    className={styles.btn_main}
                    disabled={status === 'scanning' || cooldown > 0}
                    onClick={() => runScan()}
                >
                    {status === 'scanning' ? 'ANALYZING...' :
                        cooldown > 0 ? `COOLDOWN (${cooldown}s)` : 'SCAN'}
                </button>
            </div>

            <div className={styles.section_header}>
                <div className={styles.line}></div>
                <h3 className={styles.h3}>ANALYTICS</h3>
                <div className={styles.line}></div>
            </div>

            <div className={styles.stats_grid}>
                <div className={styles.stat_card}>
                    <span className={styles.stat_label}>Total Lines</span>
                    <span className={styles.stat_value}>{fileStats.lines.toLocaleString()}</span>
                </div>
                <div className={styles.stat_card}>
                    <span className={styles.stat_label}>Threats Detected</span>
                    <span className={styles.stat_value} style={{ color: 'var(--accent)' }}>
                        {fileStats.threats.toLocaleString()}
                    </span>
                </div>
                <div className={styles.stat_card}>
                    <span className={styles.stat_label}>Execution Time</span>
                    <span className={styles.stat_value} style={{ color: 'var(--info)' }}>
                        {fileStats.time}ms
                    </span>
                </div>
                <div className={styles.stat_card}>
                    <span className={styles.stat_label}>Grade</span>
                    <span className={styles.stat_value} style={{
                        color: results?.maxSev === 'critical' ? 'var(--accent)' : 'var(--safe)'
                    }}>
                        {results ? (
                            results.maxSev === 'critical' ? 'F' :
                                results.maxSev === 'high' ? 'D' :
                                    results.maxSev === 'medium' ? 'B' : 'A'
                        ) : '-'}
                    </span>
                </div>
            </div>

            {results && (
                <div id="results" ref={resultsRef} className={styles.results_container}>
                    <div className={`${styles.report_header} ${results.findings.length > 0 ? styles.danger_bg : styles.safe_bg}`}>
                        <span>{results.findings.length === 0 ? 'CLEAN SCAN' : `${results.maxSev.toUpperCase()} RISK`}</span>
                        <span>{results.findings.length} FINDINGS</span>
                    </div>

                    {results.criticals && results.criticals.length > 0 && (
                        <div className={styles.critical_alert_box}>
                            <div className={styles.critical_alert_header}>
                                <span className={styles.pulse_icon}>!</span>
                                <h4 style={{ margin: 5 }}>IMMEDIATE ATTENTION REQUIRED</h4>
                            </div>
                            <div className={styles.critical_line_list}>
                                {results.criticals.map((c, i) => (
                                    <div key={i} className={styles.critical_line_item}>
                                        <span className={styles.line_tag}>LINE {c.lineNum}</span>
                                        <span className={styles.name_tag}>{c.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div id="threat-list" className={styles.threat_list}>
                        {results.findings.map((threat, index) => {
                            const isExpanded = expandedIndex.includes(index);

                            return (
                                <div
                                    key={index}
                                    className={`${styles.threat_item} ${isExpanded ? styles.active : ''}`}
                                    onClick={() => toggleAccordion(index)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span className={`${styles.severity_badge} ${styles['sev-' + threat.severity]}`}>
                                                {threat.severity}
                                            </span>
                                            <strong className={styles.strong}>{threat.name}</strong>
                                        </div>
                                        <span style={{ opacity: 0.3 }}>{isExpanded ? '▲' : '▼'}</span>
                                    </div>
                                    <div className={styles.threat_desc}>{threat.desc}</div>

                                    {isExpanded && (
                                        <div className={styles.accordion_content}>
                                            <div className={styles.threat_detail_text}>{threat.detail}</div>
                                            <div
                                                className={styles.match_box}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    cursor: 'default',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '5px'
                                                }}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    fontSize: '10px',
                                                    color: 'rgba(255,255,255,0.3)'
                                                }}>
                                                    <span>LINE {threat.lineNum}</span>
                                                    <button
                                                        onClick={() => copyToClipboard(threat.snippet)}
                                                        className={styles.copy_btn}
                                                    >
                                                        COPY
                                                    </button>
                                                </div>

                                                <code
                                                    onCopy={(e) => e.stopPropagation()}
                                                    className={styles.code_block}
                                                    style={{
                                                        cursor: 'text',
                                                        userSelect: 'text',
                                                        wordBreak: 'break-all',
                                                        display: 'block'
                                                    }}
                                                >
                                                    {threat.snippet}
                                                </code>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Scanner;